import Button from '@sportnet/ui/Button';
import Modal, { ModalActions, ModalContent } from '@sportnet/ui/Modal';
import Segment from '@sportnet/ui/Segment';
import SegmentHeader from '@sportnet/ui/Segment/Header';
import { Table, Tbody, Td, Th, Thead, Tr } from '@sportnet/ui/Table';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { __ } from '../../utilities';

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
  color: white;
  background-color: ${({ status }) => {
    switch (status) {
      case 'completed':
        return '#28a745';
      case 'pending':
        return '#ffc107';
      case 'failed':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  }};
`;

const ErrorMessage = styled.span`
  color: #dc3545;
`;

const InfoRow = styled.div`
  display: flex;
  margin-bottom: 8px;
  align-items: center;
`;

const InfoLabel = styled.span`
  font-weight: bold;
  margin-right: 8px;
  min-width: 150px;
`;

const ClickableTr = styled(Tr)`
  cursor: pointer;
  &:hover {
    background-color: #f5f5f5;
  }
`;

const JsonPre = styled.pre`
  background-color: #1e1e1e;
  padding: 16px;
  border-radius: 4px;
  overflow: auto;
  font-size: 12px;
  font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
  margin: 0;
  color: #d4d4d4;
  line-height: 1.5;
  max-height: 400px;
`;

const JsonKey = styled.span`
  color: #9cdcfe;
`;

const JsonString = styled.span`
  color: #ce9178;
`;

const JsonNumber = styled.span`
  color: #b5cea8;
`;

const JsonBoolean = styled.span`
  color: #569cd6;
`;

const JsonNull = styled.span`
  color: #569cd6;
`;

const NscSyncStatus = ({ fetchStatus, title }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notSynced, setNotSynced] = useState(false);
  const [data, setData] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      setNotSynced(false);
      try {
        const response = await fetchStatus();
        setData(response);
      } catch (e) {
        if (e.details?.statusCode === 404) {
          setNotSynced(true);
        } else {
          setError(
            e.details?.userinfo ||
              __('Pri získavaní stavu synchronizácie nastala chyba.'),
          );
        }
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [fetchStatus]);

  const formatDate = dateString => {
    if (!dateString) return '-';
    return moment(dateString).format('DD.MM.YYYY HH:mm:ss');
  };

  const renderJsonValue = (value, indent = 0) => {
    const spaces = '  '.repeat(indent);

    if (value === null) {
      return <JsonNull>null</JsonNull>;
    }

    if (typeof value === 'boolean') {
      return <JsonBoolean>{value.toString()}</JsonBoolean>;
    }

    if (typeof value === 'number') {
      return <JsonNumber>{value}</JsonNumber>;
    }

    if (typeof value === 'string') {
      return <JsonString>"{value}"</JsonString>;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) return '[]';
      return (
        <>
          {'[\n'}
          {value.map((item, i) => (
            <React.Fragment key={i}>
              {spaces} {renderJsonValue(item, indent + 1)}
              {i < value.length - 1 ? ',\n' : '\n'}
            </React.Fragment>
          ))}
          {spaces}
          {']'}
        </>
      );
    }

    if (typeof value === 'object') {
      const keys = Object.keys(value);
      if (keys.length === 0) return '{}';
      return (
        <>
          {'{\n'}
          {keys.map((key, i) => (
            <React.Fragment key={key}>
              {spaces} <JsonKey>"{key}"</JsonKey>:{' '}
              {renderJsonValue(value[key], indent + 1)}
              {i < keys.length - 1 ? ',\n' : '\n'}
            </React.Fragment>
          ))}
          {spaces}
          {'}'}
        </>
      );
    }

    return String(value);
  };

  return (
    <>
      <Segment
        raised
        loading={isLoading}
        header={
          <SegmentHeader size="s">
            {title || __('Stav synchronizácie s NŠC')}
          </SegmentHeader>
        }
      >
        {error ? (
          <ErrorMessage>{error}</ErrorMessage>
        ) : notSynced ? (
          <span>{__('Používateľ zatiaľ nebol synchronizovaný s NŠC.')}</span>
        ) : data ? (
          <>
            <InfoRow>
              <InfoLabel>{__('Aktuálny stav:')}</InfoLabel>
              <StatusBadge status={data.current_status}>
                {data.current_status || '-'}
              </StatusBadge>
            </InfoRow>

            {data.current_status === 'failed' && data.current_error && (
              <InfoRow>
                <InfoLabel>{__('Chyba:')}</InfoLabel>
                <ErrorMessage>{data.current_error}</ErrorMessage>
              </InfoRow>
            )}

            <InfoRow>
              <InfoLabel>{__('Posledná zmena:')}</InfoLabel>
              <span>{formatDate(data.last_updated)}</span>
            </InfoRow>
          </>
        ) : null}
      </Segment>

      {!error && data && data.batches && data.batches.length > 0 && (
        <Segment
          raised
          header={<SegmentHeader size="s">{__('História')}</SegmentHeader>}
        >
          <Table>
            <Thead>
              <Tr>
                <Th>{__('Batch ID')}</Th>
                <Th>{__('ISS Profile ID')}</Th>
                <Th>{__('Stav')}</Th>
                <Th>{__('Odoslané')}</Th>
                <Th>{__('Skontrolované')}</Th>
                <Th>{__('Dokončené')}</Th>
                <Th>{__('Chyba')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {data.batches.map((batch, index) => (
                <ClickableTr
                  key={batch.batch_id || index}
                  onClick={() => setSelectedBatch(batch)}
                >
                  <Td>{batch.batch_id || '-'}</Td>
                  <Td>{batch.iss_profile_id || '-'}</Td>
                  <Td>
                    <StatusBadge status={batch.status}>
                      {batch.status || '-'}
                    </StatusBadge>
                  </Td>
                  <Td>{formatDate(batch.submitted_at)}</Td>
                  <Td>{formatDate(batch.checked_at)}</Td>
                  <Td>{formatDate(batch.completed_at)}</Td>
                  <Td>{batch.error || '-'}</Td>
                </ClickableTr>
              ))}
            </Tbody>
          </Table>
        </Segment>
      )}

      <Modal
        isOpen={!!selectedBatch}
        handleClose={() => setSelectedBatch(null)}
      >
        <ModalContent>
          <SegmentHeader size="s">{__('Detail batch')}</SegmentHeader>
          <JsonPre>{selectedBatch && renderJsonValue(selectedBatch)}</JsonPre>
        </ModalContent>
        <ModalActions style={{ justifyContent: 'flex-end' }}>
          <Button primary onClick={() => setSelectedBatch(null)}>
            {__('Zavrieť')}
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
};

NscSyncStatus.propTypes = {
  fetchStatus: PropTypes.func.isRequired,
  title: PropTypes.string,
};

NscSyncStatus.defaultProps = {
  title: null,
};

export default NscSyncStatus;
